import DynamicsLauncher from './components/common/DynamicsLauncher';
import React, { useMemo, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ProjectDataProvider, useProjectData } from './context/ProjectDataContext';
import LoginPage from './components/auth/LoginPage';
import ProjectSelection from './components/project/ProjectSelection';
import DashboardWithProject from './components/dashboard/DashboardWithProject';
import HelpdeskPage from './components/dashboard/HelpdeskPage';
import ClientTestingPage from './components/dashboard/ClientTestingPage';
import UserStoriesByStatusPage from './components/dashboard/UserStoriesByStatusPage';
import MilestoneUserStoriesPage from './components/dashboard/MilestoneUserStoriesPage';
import AnalyticsPage from './components/dashboard/AnalyticsPage';
import TeamPerformancePage from './components/dashboard/TeamPerformancePage';
import ChatbotPage from './components/dashboard/ChatbotPage';
import SprintPage from './components/dashboard/SprintPage';
import DeploiementPage from './components/dashboard/DeploiementPage';
import LivraisonsPage from './components/dashboard/LivraisonsPage';
import TeamTvDashboard from './components/dashboard/TeamTvDashboard';
import DocumentationPage from './components/dashboard/DocumentationPage';
import ApiDebug from './components/debug/ApiDebug';
import MainLayout from './components/layout/MainLayout';
import PersonalIntercomListener from './components/layout/PersonalIntercomListener';
import VoiceCallOverlay from './components/layout/VoiceCallOverlay';
import { VoiceCallProvider } from './context/VoiceCallContext';
import backendApi from './services/backendApi';
import { DYNAMICS_ENABLED } from './config/features';

// Composant de protection des routes
const ProtectedRoute = ({ children }) => {
  const { user, loading, logout, isInternalUser } = useAuth();

  // Lire les données depuis le contexte partagé (un seul appel API pour toute l'app)
  const { isAdmin, tickets = [], userStories = [], refresh, loading: dataLoading } = useProjectData();

  // Vide tous les caches backend puis re-fetch les données
  const handleRefresh = useCallback(async () => {
    await Promise.allSettled([
      backendApi.flushCache(),
      backendApi.flushAnalyticsCache(),
    ]);
    refresh();
  }, [refresh]);

  // Calculer le nombre de tickets ouverts (non résolus)
  const openTicketsCount = useMemo(() => {
    if (!tickets || tickets.length === 0) return 0;

    return tickets.filter(ticket => {
      const stageName = ticket.stageName?.toLowerCase() || '';
      // Un ticket est ouvert s'il n'est PAS résolu/fermé/done
      const isResolved = stageName.includes('résolu') ||
                        stageName.includes('solved') ||
                        stageName.includes('fermé') ||
                        stageName.includes('closed') ||
                        stageName.includes('done');
      return !isResolved;
    }).length;
  }, [tickets]);

  // Calculer le nombre de user stories en cours (non "Done")
  const activeUserStoriesCount = useMemo(() => {
    if (!userStories || userStories.length === 0) return 0;

    return userStories.filter(story => {
      return story.category !== 'Done';
    }).length;
  }, [userStories]);

  // Calculer le nombre de user stories en "Tests à valider"
  const testClientStoriesCount = useMemo(() => {
    if (!userStories || userStories.length === 0) return 0;

    return userStories.filter(story => {
      return story.category === 'Tests à valider' || story.category === 'Test Client';
    }).length;
  }, [userStories]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <MainLayout
      isAdmin={isAdmin}
      user={user}
      onLogout={logout}
      onRefresh={handleRefresh}
      isRefreshing={dataLoading}
      openTicketsCount={openTicketsCount}
      activeUserStoriesCount={activeUserStoriesCount}
      testClientStoriesCount={testClientStoriesCount}
      isInternalUser={isInternalUser}
    >
      {children}
    </MainLayout>
  );
};

const TicketSituationRoute = () => {
  return <UserStoriesByStatusPage situation />;
};

const LegacyUserStoriesRedirect = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const status = location.state?.status || params.get('stage') || 'All';
  if (status === 'Tests à valider' || status === 'Test Client') {
    params.delete('stage');
    const query = params.toString();
    return <Navigate to={`/dashboard/client-testing${query ? `?${query}` : ''}`} replace />;
  }
  if (status && status !== 'All') params.set('stage', status);
  else params.delete('stage');
  const query = params.toString();
  return <Navigate to={`/dashboard/ticket-situation${query ? `?${query}` : ''}`} replace />;
};

// Dashboard TV interne, conservé dans le layout principal pour garder la navigation.
const TeamTvRoute = () => {
  const { user, loading, isInternalUser } = useAuth();

  if (loading) {
    return <div className="min-h-screen bg-[#080d0a]" role="status" aria-label="Chargement du dashboard équipe" />;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!isInternalUser) return <Navigate to="/dashboard/tickets" replace />;
  return (
    <ProtectedRoute>
      <TeamTvDashboard currentUserId={user.uid} />
    </ProtectedRoute>
  );
};

// Documentation accessible aux utilisateurs authentifiés.
const DocumentationRoute = () => {
  const { user, loading } = useAuth();

  if (loading) return <div className="min-h-screen bg-gray-50" role="status" aria-label="Chargement" />;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <ProtectedRoute>
      <DocumentationPage />
    </ProtectedRoute>
  );
};

// L'écran équipe est la page d'accueil des internes.
const HomeRoute = () => {
  const { isInternalUser } = useAuth();
  return isInternalUser
    ? <Navigate to="/team-tv" replace />
    : <ProjectSelection />;
};

// Le dashboard client est accessible aux clients comme aux utilisateurs internes.
const DashboardRoute = () => <DashboardWithProject />;

// Layout global de l'application
const AppLayout = ({ children }) => {
  return (
    <div className="min-h-screen bg-gray-50">
      {children}
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <VoiceCallProvider>
        <ProjectDataProvider>
        <AppLayout>
          {DYNAMICS_ENABLED && <DynamicsLauncher />}
          <PersonalIntercomListener />
          <VoiceCallOverlay />
          <Routes>
            {/* Route de connexion */}
            <Route path="/login" element={<LoginPage />} />

            {/* Route de debug (développement uniquement) */}
            <Route path="/debug" element={<ApiDebug />} />

            {/* Dashboard mural avec navigation (utilisateurs internes) */}
            <Route path="/team-tv" element={<TeamTvRoute />} />

            {/* Route de sélection de projet (protégée) */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <HomeRoute />
                </ProtectedRoute>
              }
            />

            {/* Route du dashboard (protégée) */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardRoute />
                </ProtectedRoute>
              }
            />

            {/* Route des tickets d'assistance (protégée) */}
            <Route
              path="/dashboard/tickets"
              element={
                <ProtectedRoute>
                  <HelpdeskPage />
                </ProtectedRoute>
              }
            />

            <Route path="/dashboard/ticket-situation" element={<ProtectedRoute><TicketSituationRoute /></ProtectedRoute>} />
            {/* Route Test Client (protégée) */}
            <Route
              path="/dashboard/client-testing"
              element={
                <ProtectedRoute>
                  <ClientTestingPage />
                </ProtectedRoute>
              }
            />

            {/* Route User Stories par Statut (protégée) */}
            <Route
              path="/dashboard/user-stories-status"
              element={
                <ProtectedRoute>
                  <LegacyUserStoriesRedirect />
                </ProtectedRoute>
              }
            />

            {/* Route US d'un jalon (protégée) */}
            <Route
              path="/dashboard/milestone-stories"
              element={
                <ProtectedRoute>
                  <MilestoneUserStoriesPage />
                </ProtectedRoute>
              }
            />

            {/* Route Chatbot temporairement désactivée */}
            <Route
              path="/dashboard/chatbot"
              element={
                <ProtectedRoute>
                  {DYNAMICS_ENABLED ? <ChatbotPage /> : <Navigate to="/dashboard" replace />}
                </ProtectedRoute>
              }
            />

            {/* Route Analytics (interne uniquement) */}
            <Route
              path="/dashboard/analytics"
              element={
                <ProtectedRoute>
                  <AnalyticsPage />
                </ProtectedRoute>
              }
            />

            {/* Route Sprint Actif (interne uniquement) */}
            <Route
              path="/dashboard/sprint"
              element={
                <ProtectedRoute>
                  <SprintPage />
                </ProtectedRoute>
              }
            />

            {/* Route Performance équipe (interne uniquement) */}
            <Route
              path="/dashboard/team-performance"
              element={
                <ProtectedRoute>
                  <TeamPerformancePage />
                </ProtectedRoute>
              }
            />

            {/* Route Déploiements (protégée) */}
            <Route
              path="/dashboard/deploiements"
              element={
                <ProtectedRoute>
                  <DeploiementPage />
                </ProtectedRoute>
              }
            />

            {/* Route Livraisons / Versions (protégée) */}
            <Route
              path="/dashboard/livraisons"
              element={
                <ProtectedRoute>
                  <LivraisonsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/dashboard/documentation"
              element={<DocumentationRoute />}
            />

            {/* Redirection par défaut */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AppLayout>
        </ProjectDataProvider>
        </VoiceCallProvider>
      </Router>
    </AuthProvider>
  );
}

export default App;
